import { useQuery } from '@tanstack/react-query';
import { Messages } from '../../../utils/message-constants';
import {
  getContact,
  getGPanelLabelsLogs,
  getLabelGroups,
  getLabels,
  getOrganizationDirectoryContacts,
  getTrashedContacts,
  getTrashedLabels,
  GPanelContactsKeys,
  getLabelContacts,
  getAllSyncs,
  getSyncsByLabelId,
  getSyncsById,
  getSyncUsersStatus,
} from './gpanel-contacts-api';

export const useLabelsQuery = () =>
  useQuery({
    queryKey: [GPanelContactsKeys.GPanelLabels],
    queryFn: () => getLabels(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('labels'),
    },
  });

export const useOrganizationDirectoryContactsQuery = () =>
  useQuery({
    queryKey: [GPanelContactsKeys.OrganizationDirectoryContacts],
    queryFn: () => getOrganizationDirectoryContacts(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading(
        'organization directory contacts'
      ),
    },
  });

export const useLabelContactsQuery = (labelId: number | undefined) =>
  useQuery({
    queryKey: [GPanelContactsKeys.LabelContacts, labelId],
    queryFn: () => getLabelContacts(labelId!),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('label contacts'),
    },
    enabled: Boolean(labelId),
  });

export const useLabelGroupsQuery = () =>
  useQuery({
    queryKey: [GPanelContactsKeys.LabelGroups],
    queryFn: () => getLabelGroups(),
    staleTime: 0,
    meta: {
      errorMessage: Messages.Errors.Network.Loading(
        'organization directory contacts'
      ),
    },
  });

export const useGPanelContactQuery = (id: string) =>
  useQuery({
    queryKey: [GPanelContactsKeys.GPanelContact, id],
    queryFn: () => getContact(id),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('contact'),
    },
    enabled: !!id,
  });

export const useTrashLabelsQuery = () =>
  useQuery({
    queryKey: [GPanelContactsKeys.TrashLabels],
    queryFn: () => getTrashedLabels(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('trashed labels'),
    },
  });

export const useTrashContactsQuery = () =>
  useQuery({
    queryKey: [GPanelContactsKeys.TrashContacts],
    queryFn: () => getTrashedContacts(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('trashed contacts'),
    },
  });

export const useGPanelLabelsLogsQuery = (labelId: number) =>
  useQuery({
    queryKey: [GPanelContactsKeys.GPanelLabelsLogs, labelId],
    queryFn: () => getGPanelLabelsLogs(labelId),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('labels logs'),
    },
  });

export const useAllSyncsQuery = () =>
  useQuery({
    queryKey: [GPanelContactsKeys.AllSyncs],
    queryFn: () => getAllSyncs(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('syncs'),
    },
  });

export const useLabelSyncsQuery = (labelId: string) =>
  useQuery({
    queryKey: [GPanelContactsKeys.LabelSyncs, labelId],
    queryFn: () => getSyncsByLabelId(labelId),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('syncs'),
    },
    enabled: Boolean(labelId),
  });

export const useSyncByIdQuery = (syncId: string) =>
  useQuery({
    queryKey: [GPanelContactsKeys.SyncById, syncId],
    queryFn: () => getSyncsById(syncId),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('sync'),
    },
  });

export const useSyncUsersStatusQuery = (syncId: string) =>
  useQuery({
    queryKey: [GPanelContactsKeys.SyncUsersStatus, syncId],
    queryFn: () => getSyncUsersStatus(syncId),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('sync'),
    },
  });
